import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Link, Text } from 'core/components';
import $syn from 'app/stores/synthetics/$syn';
import { TestCollection } from 'app/stores/synthetics/synthTypes';

interface SynthTestProps {
  /** The synthetic test ID */
  testId: string | number;
  /** Text size */
  small?: boolean;
  /** Whether to make the test name clickable (links to test detail page) */
  clickable?: boolean;
}

/**
 * Renders a synthetic test name by looking up the test from the $syn store.
 */
function SynthTest({ testId, small = false, clickable = true }: SynthTestProps) {
  // Access tests from store inside component (not at module load time)
  const synTests = $syn.tests as TestCollection | null;

  // Ensure tests are fetched once - rely on hasFetched to prevent duplicate fetches
  useEffect(() => {
    if (synTests && !synTests.hasFetched) {
      synTests.fetch();
    }
  }, [synTests]);

  if (!testId || !synTests) {
    return (
      <Text small={small} muted>
        --
      </Text>
    );
  }

  // Access models to ensure MobX observes the collection and re-renders when tests are loaded
  const { models, hasFetched } = synTests;

  // get() can return Model | Model[], so narrow the type
  const testResult = synTests.get(testId);
  const test = Array.isArray(testResult) ? testResult[0] : testResult;

  if (!test) {
    // Test not found - show loading if fetch is in progress, otherwise show ID as fallback
    if (!hasFetched || models.length === 0) {
      return (
        <Text small={small} muted>
          Loading Test {testId}...
        </Text>
      );
    }
    return (
      <Text small={small} muted>
        Test {testId}
      </Text>
    );
  }

  // Use display_name if available, fall back to name or id
  const displayName = test.get('display_name') || test.get('name') || `Test ${testId}`;
  const testDetailUrl = `/v4/synthetics/tests/${testId}/results`;

  if (clickable) {
    return (
      <Link to={testDetailUrl} aria-label="View test details (opens in new tab)" blank rel="noopener noreferrer">
        <Text small={small}>{displayName}</Text>
      </Link>
    );
  }

  return <Text small={small}>{displayName}</Text>;
}

export default observer(SynthTest);
